/**
 * MODULE: _shared/frontend-shell/src/features/crm/constants/crmTabs.ts
 * PURPOSE: CRM base path and primary tab ids, labels, route paths for CrmTabNav; URL→tab match and shell **`document.title`** helpers
 * DEPENDENCIES: ../types/crm.types (CrmTab)
 * PUBLIC_API: CRM_BASE_PATH, CRM_TABS, CrmTabConfig, matchCrmTabForPathname, crmShellBaseDocumentTitle
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 50/150
 * RECENT: 2026-07-05 — My Company tab at /crm/company; Products tab at /crm/products.
 * RECENT_PRIOR: 2026-06-22 [2.2.1470] — Territory tab label **My accounts**; primary route **`/crm/accounts`** — View Current Accounts sprint.
 * RECENT_PRIOR: 2026-05-11 [2.2.943] **`matchCrmTabForPathname`**
 * RECENT_PRIOR: 2026-05-08 **`CRM_TABS`** **`tasks`** (**`/crm/tasks`**) after Deals — root **`CHANGELOG` [2.2.884]`**
 * RECENT_PRIOR: 2026-04-30 [2.2.730] **`matchCrmTabForPathname`**, **`crmShellBaseDocumentTitle`** — CHANGELOG [2.2.730].
 */

import type { CrmTab } from '../types/crm.types';
import { isLikelyAccountDetailId } from '../../../routes/builders';

/** Canonical CRM shell prefix — nested routes live under this path */
export const CRM_BASE_PATH = '/crm';

export const CRM_TABS = [
  { id: 'home', label: 'Home', path: '/crm' },
  { id: 'my-quota', label: 'My quota', path: '/crm/my-quota' },
  { id: 'territory', label: 'My accounts', path: '/crm/accounts' },
  { id: 'contacts', label: 'Contacts', path: '/crm/contacts' },
  { id: 'deals', label: 'Deals', path: '/crm/deals' },
  { id: 'company', label: 'My Company', path: '/crm/company' },
  { id: 'products', label: 'Products', path: '/crm/products' },
  { id: 'tasks', label: 'Tasks', path: '/crm/tasks' },
  { id: 'pipeline', label: 'Pipeline Generation', path: '/crm/pipeline-generation' },
] as const satisfies ReadonlyArray<{ id: CrmTab; label: string; path: string }>;

export type CrmTabConfig = (typeof CRM_TABS)[number];

/**
 * Match URL to the CRM tab row (same rules as shell `document.title` / `pathnameToTab`).
 * Uses longest tab path match via `startsWith` for nested routes; `/crm` only matches the home row.
 */
export function matchCrmTabForPathname(pathname: string): CrmTabConfig {
  const p = pathname.replace(/\/$/, '') || pathname;
  // My accounts portfolio list (detail lives at /accounts/:id).
  if (p === `${CRM_BASE_PATH}/accounts`) {
    const territory = CRM_TABS.find((t) => t.id === 'territory');
    if (territory) return territory;
  }
  const accountDetailSeg = p.match(/^\/accounts\/([^/]+)$/)?.[1];
  if (accountDetailSeg && isLikelyAccountDetailId(accountDetailSeg)) {
    const territory = CRM_TABS.find((t) => t.id === 'territory');
    if (territory) return territory;
  }
  // Legacy territory URLs still highlight My accounts until redirects fully retire.
  if (p === `${CRM_BASE_PATH}/territory` || p.startsWith(`${CRM_BASE_PATH}/territory/`)) {
    const territory = CRM_TABS.find((t) => t.id === 'territory');
    if (territory) return territory;
  }
  const matched = CRM_TABS.find((t) => {
    if (t.path === CRM_BASE_PATH) return p === CRM_BASE_PATH;
    return p === t.path || p.startsWith(`${t.path}/`);
  });
  return matched ?? CRM_TABS[0];
}

/** Base `document.title` segment for the AI assistant (`stripTitleBranding` keeps `… · CRM`). */
export function crmShellBaseDocumentTitle(pathname: string): string {
  return `${matchCrmTabForPathname(pathname).label} · CRM · Brazn`;
}
